import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ZoomIn, Sparkles, Ruler, AlertTriangle, Crosshair } from "lucide-react";
import { StampSvg } from "@/components/StampSvg";
import { borderInset, refKey, recenter, centerAll, isOffCenter } from "@/lib/layout";

const PX_MM = 96 / 25.4;
const r1 = (v) => Math.round(v * 10) / 10;

function RulerBar({ length, scale, vertical }) {
  const step = scale > 9 ? 1 : 2;
  const ticks = [];
  for (let i = 0; i <= length; i += step) {
    const major = i % 10 === 0, mid = i % 5 === 0;
    const len = major ? 10 : mid ? 7 : 4;
    const p = i * scale;
    ticks.push(
      vertical ? <line key={i} x1={18 - len} x2={18} y1={p} y2={p} stroke="#64748b" strokeWidth="1" /> : <line key={i} y1={18 - len} y2={18} x1={p} x2={p} stroke="#64748b" strokeWidth="1" />
    );
    if (major && i > 0)
      ticks.push(
        <text key={`t${i}`} x={vertical ? 1 : p + 2} y={vertical ? p - 2 : 8} fontSize="8" fill="#475569" fontFamily="JetBrains Mono">
          {i}
        </text>
      );
  }
  return vertical ? <svg width={18} height={length * scale + 1}>{ticks}</svg> : <svg width={length * scale + 1} height={18}>{ticks}</svg>;
}

export function StampCanvas({ product, design, setDesign, layout, ink, selected, setSelected, onWarnings }) {
  const wrapRef = useRef(null);
  const svgRef = useRef(null);
  const [zoom, setZoom] = useState("fit");
  const [fit, setFit] = useState(6);
  const [realistic, setRealistic] = useState(false);
  const [boxes, setBoxes] = useState({});
  const [tick, setTick] = useState(0);
  const W = product.width_mm, H = product.height_mm;
  const safe = (product.config.margin_mm ?? 1) + borderInset(design.border) * 0;

  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      const aw = el.clientWidth - 60, ah = Math.max(260, Math.min(520, window.innerHeight * 0.5));
      setFit(Math.max(2, Math.min(aw / W, ah / H, 16)));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [W, H]);

  useEffect(() => {
    const f = () => setTick((t) => t + 1);
    document.fonts.addEventListener("loadingdone", f);
    document.fonts.ready.then(f);
    return () => document.fonts.removeEventListener("loadingdone", f);
  }, []);

  const scale = zoom === "fit" ? fit : zoom * PX_MM;

  useLayoutEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;
    const out = {}, warn = [];
    const R = Math.min(W, H) / 2, round = product.shape === "round" || product.shape === "oval";
    svg.querySelectorAll("g[data-ref]").forEach((g) => {
      const b = g.getBBox();
      const k = g.getAttribute("data-ref");
      out[k] = { x: b.x, y: b.y, w: b.width, h: b.height };
      if (!b.width) return;
      let over = b.x < -0.05 || b.y < -0.05 || b.x + b.width > W + 0.05 || b.y + b.height > H + 0.05;
      if (round && k.startsWith("graphic")) {
        const corners = [[b.x, b.y], [b.x + b.width, b.y], [b.x, b.y + b.height], [b.x + b.width, b.y + b.height]];
        over = over || corners.some(([x, y]) => Math.hypot(x - W / 2, y - H / 2) > R + 0.3);
      }
      if (over) warn.push(k);
    });
    const keys = Object.keys(out).filter((k) => out[k].w);
    const hit = (a, b) => a.x < b.x + b.w - 0.2 && b.x < a.x + a.w - 0.2 && a.y < b.y + b.h - 0.2 && b.y < a.y + a.h - 0.2;
    keys.filter((k) => k.startsWith("line")).forEach((t) => {
      if (keys.some((g) => g.startsWith("graphic") && hit(out[t], out[g])) && !warn.includes(t)) warn.push(t);
    });
    setBoxes(out);
    onWarnings?.(warn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layout, tick, scale]);

  const startDrag = (ref, e, mode = "move") => {
    e.preventDefault();
    e.stopPropagation();
    setSelected(ref);
    const sx = e.clientX, sy = e.clientY;
    const orig = ref.kind === "line" ? { ...design.lines[ref.i] } : { ...design.graphics[ref.i] };
    const lineResize = ref.kind === "line" && mode === "resize";
    const b0 = boxes[refKey(ref)] || {}, box = { w: b0.w || 1, h: b0.h || 1 };
    const shown = layout.elements.find((el) => el.ref.kind === "line" && el.ref.i === ref.i)?.size_pt || orig.size_pt;
    const cfg = product.config;
    let resized = false;
    const move = (ev) => {
      const dx = (ev.clientX - sx) / scale, dy = (ev.clientY - sy) / scale;
      setDesign((d) => {
        if (lineResize) {
          const k = !orig.curve && orig.align !== "center" ? 1 : 2;
          const f = Math.max((box.w + k * dx) / box.w, (box.h + dy) / box.h);
          const size_pt = Math.max(cfg.min_font_pt, Math.min(cfg.max_font_pt, Math.round(shown * f * 2) / 2));
          resized = resized || size_pt !== shown;
          return { ...d, lines: d.lines.map((l, i) => (i === ref.i ? { ...l, size_pt, manual: true } : l)) };
        }
        if (ref.kind === "line")
          return { ...d, lines: d.lines.map((l, i) => (i === ref.i ? { ...l, offset_x: l.curve ? 0 : r1((orig.offset_x || 0) + dx), offset_y: r1((orig.offset_y || 0) + dy) } : l)) };
        return {
          ...d,
          graphics: d.graphics.map((g, i) => {
            if (i !== ref.i) return g;
            if (mode === "resize") {
              const w = Math.max(2, r1(orig.w + dx));
              return { ...g, w, h: r1(w / (orig.w / orig.h)) };
            }
            return { ...g, x: r1(orig.x + dx), y: r1(orig.y + dy) };
          }),
        };
      });
    };
    const up = () => {
      if (lineResize && resized) {
        setDesign(recenter);
        toast.success("Riga ridimensionata: testo ricentrato automaticamente");
      }
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  const sel = selected && boxes[refKey(selected)];
  const zoomBtn = (z, label, testid) => (
    <button key={label} data-testid={testid} onClick={() => setZoom(z)}
      className={`press px-2.5 py-1 text-xs font-mono rounded ${zoom === z ? "bg-brand-navy text-white" : "bg-white text-slate-700 hover:bg-slate-200"}`}>
      {label}
    </button>
  );

  return (
    <div ref={wrapRef} className="ink-grid relative w-full min-w-0 flex flex-col items-center rounded-lg border border-slate-200 p-4 pb-6 min-h-[360px]" data-testid="stamp-canvas">
      <div className="flex flex-wrap items-center gap-1.5 self-stretch justify-between mb-4">
        <div className="flex items-center gap-1 bg-white/80 backdrop-blur rounded-md p-1 border border-slate-200">
          <ZoomIn className="w-3.5 h-3.5 mx-1 text-slate-500" />
          {zoomBtn("fit", "Adatta", "zoom-fit-btn")}
          {zoomBtn(1, "1:1", "zoom-real-btn")}
          {zoomBtn(2, "2x", "zoom-2x-btn")}
          {zoomBtn(4, "4x", "zoom-4x-btn")}
        </div>
        <div className="flex items-center gap-2">
          <span className="font-mono text-[11px] bg-brand-navy text-white px-2 py-1 rounded flex items-center gap-1" data-testid="stamp-dimensions-badge">
            <Ruler className="w-3 h-3" /> {W} × {H} mm
          </span>
          <button data-testid="toggle-realistic-btn" onClick={() => setRealistic((v) => !v)}
            className={`press text-xs px-2.5 py-1 rounded border flex items-center gap-1 ${realistic ? "bg-brand-yellow text-brand-navy border-brand-yellow" : "bg-white border-slate-200 text-slate-700"}`}>
            <Sparkles className="w-3 h-3" /> {realistic ? "Impronta reale" : "Vettoriale"}
          </button>
        </div>
      </div>
      <div className="overflow-auto max-w-full flex-1 flex items-center justify-center w-full">
        <div className="inline-grid" style={{ gridTemplateColumns: "18px auto", gridTemplateRows: "18px auto" }}>
          <div />
          <RulerBar length={W} scale={scale} />
          <RulerBar length={H} scale={scale} vertical />
          <div className="relative shadow-[0_10px_40px_-12px_rgba(15,23,42,0.35)]" onPointerDown={() => setSelected(null)} style={{ borderRadius: product.shape === "round" || product.shape === "oval" ? "50%" : 2 }}>
            <StampSvg svgRef={svgRef} layout={layout} color={ink} scale={scale} realistic={realistic} selected={selected} onElDown={startDrag} showSafe={safe} />
            {sel && (
              <div className="absolute pointer-events-none border border-dashed border-brand-navy bg-brand-yellow/10" data-testid="selection-box"
                style={{ left: sel.x * scale - 3, top: sel.y * scale - 3, width: sel.w * scale + 6, height: sel.h * scale + 6 }}>
                <span onPointerDown={(e) => startDrag(selected, e, "resize")} data-testid={selected.kind === "line" ? "line-resize-handle" : "resize-handle"}
                  title="Trascina per ridimensionare"
                  className="pointer-events-auto touch-none absolute -right-2 -bottom-2 w-4 h-4 bg-brand-navy border-2 border-white rounded-sm cursor-nwse-resize shadow" />
              </div>
            )}
          </div>
        </div>
      </div>
      {isOffCenter(design) && (
        <div data-testid="realign-warning" className="mt-4 flex flex-wrap items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          <AlertTriangle className="w-4 h-4 shrink-0" /> Il testo non è più centrato.
          <button data-testid="realign-btn" onClick={() => setDesign(centerAll)} className="press inline-flex items-center gap-1 rounded bg-brand-navy text-white px-2.5 py-1 font-semibold">
            <Crosshair className="w-3 h-3" /> Centra e allinea
          </button>
        </div>
      )}
      <p className="text-[11px] text-slate-500 mt-4 font-mono text-center">Clicca una riga e trascina il quadratino blu per ingrandirla o rimpicciolirla · trascina testi e grafiche per spostarli · la linea tratteggiata è il margine di sicurezza</p>
      <p className="text-xs font-semibold text-brand-navy mt-1" data-testid="canvas-delete-lines-hint">Cancella le righe che non ti servono per ridimensionare il testo</p>
      {zoom === 1 && <p className="text-[11px] text-slate-400 font-mono">1:1 indicativo: la dimensione reale dipende dal tuo schermo</p>}
    </div>
  );
}
